import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  BusinessFilters,
  BusinessList,
  BusinessInfoModal,
} from "../../../ui/Business";
import { Icon, Button } from "../../../ui";
import { useBusinesses, useUrlStates } from "@finitless/shared/hooks";
import {
  BUSINESS_FILTER_TABS,
  BUSINESS_ITEMS_PER_PAGE,
} from "@finitless/shared/constants";
import "../../../catalog-ordering.css";

const StoresPage = ({ selectedBrand }) => {
  const { t } = useTranslation("stores");
  const navigate = useNavigate();

  const initialUrlStates = useMemo(
    () => ({
      filter: "enabled",
      searchTerm: "",
      currentPage: "1",
    }),
    [],
  );

  // URL-persisted state (tab, search, page)
  const [urlStates, updateUrlState, updateUrlStates] =
    useUrlStates(initialUrlStates);

  const [filter, setFilter] = useState(urlStates.filter || "enabled");
  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  }, [searchTerm]);

  // Parallel count queries (pageSize: 1 — only need pagination.total)
  const { data: allData } = useBusinesses({
    enabledFilter: "all",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  const { data: enabledData } = useBusinesses({
    enabledFilter: "enabled",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  const { data: disabledData } = useBusinesses({
    enabledFilter: "disabled",
    searchTerm,
    page: 1,
    pageSize: 1,
  });

  // Main data query for current tab + page
  const { data, isLoading, error, refetch } = useBusinesses({
    enabledFilter: filter,
    searchTerm,
    page: currentPage,
    pageSize: BUSINESS_ITEMS_PER_PAGE,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};

  const tabCounts = {
    all: allData?.pagination?.total || 0,
    enabled: enabledData?.pagination?.total || 0,
    disabled: disabledData?.pagination?.total || 0,
  };

  const getFilterLabel = (filterKey, fallbackLabel) => {
    const labelByFilter = {
      enabled: t("tabs.active"),
      disabled: t("tabs.inactive"),
      all: t("tabs.all"),
    };
    return labelByFilter[filterKey] || fallbackLabel;
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    updateUrlStates({ filter: newFilter, currentPage: "1" });
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    updateUrlState("searchTerm", term);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlStates({ searchTerm: "", currentPage: "1" });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInfoClick = () => {
    setIsInfoModalOpen(true);
  };

  const handleCreateBusiness = () => {
    navigate("/menu/stores/create/address");
  };

  return (
    <div
      className="ordering-catalog-surface"
      data-ordering-expression="product"
    >
      <PageHeader
        title={t("header.title")}
        subtitle={t("header.subtitle")}
        selectedBrand={selectedBrand}
      />

      <div className="space-y-4 sm:space-y-6">
        {/* Action Buttons */}
        <div className="ordering-catalog-actions flex flex-col justify-end gap-3 sm:flex-row sm:items-center sm:gap-3">
          <Button
            onClick={handleCreateBusiness}
            variant="default"
            size="default"
            className="h-9 w-full sm:h-10 sm:w-auto"
          >
            <Icon name="Plus" className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">
              {t("actions.createBusiness")}
            </span>
          </Button>
          <Button
            variant="ghost"
            onClick={handleInfoClick}
            className="border-border bg-card text-foreground hover:bg-secondary focus:ring-ring inline-flex h-9 w-full items-center justify-center rounded-md border px-3 py-2 text-xs font-medium focus:ring-2 focus:ring-offset-2 focus:outline-none sm:h-10 sm:w-auto sm:text-sm"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm sm:text-base">{t("actions.info")}</span>
          </Button>
        </div>

        {/* Status Tabs */}
        <div className="border-border border-b">
          <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
            {BUSINESS_FILTER_TABS.map(({ key, label, color }) => {
              const count = tabCounts[key] || 0;
              return (
                <Button
                  variant="ghost"
                  key={key}
                  onClick={() => handleFilterChange(key)}
                  className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                    filter === key
                      ? `border-current ${color}`
                      : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
                  }`}
                >
                  {getFilterLabel(key, label)} ({count})
                </Button>
              );
            })}
          </nav>
        </div>

        {/* Search and Refresh */}
        <BusinessFilters
          searchTerm={searchInput}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

        {/* Business List */}
        <BusinessList
          businesses={businesses}
          pagination={pagination}
          isLoading={isLoading}
          error={error}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          pageSize={BUSINESS_ITEMS_PER_PAGE}
        />
      </div>

      {/* Info Modal */}
      <BusinessInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
};

export default StoresPage;
